import { COOKIE_KEY_HLW_CONSENT, getCookie, setCookie } from "@packages/sdk";
import type { NextRequest, NextResponse } from "next/server";

const STRICT_CONSENT_REGIONS = [
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "IA",
  "IN",
  "KY",
  "MD",
  "MN",
  "MT",
  "NE",
  "NH",
  "NJ",
  "OR",
  "RI",
  "TN",
  "TX",
  "UT",
  "VA",
] as const;

const STRICT_CONSENT_COUNTRIES = new Set([
  // EU
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  // EEA (non-EU but GDPR applies)
  "IS",
  "LI",
  "NO",
  // UK GDPR
  "GB",
  // Swiss nFADP (GDPR-equivalent)
  "CH",
  // Brazil LGPD
  "BR",
  // South Africa POPIA
  "ZA",
]);

const COOKIE_MAX_AGE_SECONDS = 60 * 24 * 60 * 60; // 60 days

export type ResolveFeatureFlagVariantOptions<T extends string> = {
  cookieKey: string;
  variants: readonly T[];
  /** When true, only users in the US get a variant; everyone else is "excluded" */
  usOnly?: boolean;
};

/**
 * Resolves a feature-flag variant from cookie or assigns one using geo + consent.
 * Persists the variant in the given cookie when assigning.
 * Use for A/B tests that share the same consent/geo rules.
 *
 * @returns The variant from cookie, or a newly assigned variant, or "excluded"
 */
export function resolveFeatureFlagVariant<T extends string>(
  request: NextRequest,
  response: NextResponse,
  options: ResolveFeatureFlagVariantOptions<T>,
): T | "excluded" {
  const { cookieKey, variants, usOnly = true } = options;

  const stored = getCookie<string>({
    cookies: request.cookies,
    key: cookieKey,
  });

  const isValidStored =
    stored &&
    (stored === "excluded" || (variants as readonly string[]).includes(stored));
  if (isValidStored) return stored as T | "excluded";

  const country =
    request.headers.get("x-vercel-ip-country") ??
    request.headers.get("CloudFront-Viewer-Country");
  const region =
    request.headers.get("x-vercel-ip-country-region") ??
    request.headers.get("CloudFront-Viewer-Country-Region");
  const isGeoInformationAvailable = Boolean(country && region);
  const isStrictConsentRegion =
    (country !== null && STRICT_CONSENT_COUNTRIES.has(country)) ||
    (region !== null &&
      STRICT_CONSENT_REGIONS.includes(
        region as (typeof STRICT_CONSENT_REGIONS)[number],
      ));

  const consentCookie = request.cookies.get(COOKIE_KEY_HLW_CONSENT)?.value;
  const hasAnalyticsConsent =
    Boolean(consentCookie) && consentCookie.includes("1");

  const canAssignVariant =
    (isGeoInformationAvailable && !isStrictConsentRegion) ||
    hasAnalyticsConsent;

  const variant: T | "excluded" =
    (usOnly && country !== "US") || !canAssignVariant
      ? "excluded"
      : (variants[Math.floor(Math.random() * variants.length)] as T);

  setCookie({
    cookies: response.cookies,
    key: cookieKey,
    value: variant,
    options: { maxAge: COOKIE_MAX_AGE_SECONDS },
  });

  return variant;
}
